import { useMutation } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { ORDER_STOP_TYPES } from '../../constants';
import { useOrderCancelMutation } from '../../hooks/useOrderCancelMutation';
import { useOrderMutation } from '../../hooks/useOrderMutation';
import { Position } from '../../hooks/usePositions';
import { ProductType } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { getDecimalPlaces } from '../../lib/math';
import { showToast } from '../../lib/showToast';
import {
  positionBothStopOrdersError,
  positionStopOrderError,
  positionStopOrdersCancellationError,
  positionStopOrdersSuccess,
} from '../../lib/toasts';
import { notNill } from '../../lib/typeGuards';
import { OrderSide } from '../../types/generated';
import { PositionStopOrder } from '../../types/orders';

type TpSlMutationResult = {
  shouldCloseModal: boolean;
};

type OperationStatus = 'success' | 'failed' | 'not_attempted';
type OrderState = 'created' | 'closed' | 'unchanged' | 'failed';

type MutationOutcomeSummary = {
  tpOutcome: OrderState;
  slOutcome: OrderState;
  toastData: {
    newTpPrice: bigint | undefined;
    newSlPrice: bigint | undefined;
    priceDecimals: number;
  };
};

const determineResultAndToast = (summary: MutationOutcomeSummary): TpSlMutationResult => {
  const { tpOutcome, slOutcome, toastData } = summary;
  const { newTpPrice, newSlPrice, priceDecimals } = toastData;

  // Error toasts are separated from success toasts
  if (tpOutcome === 'failed' && slOutcome === 'failed') {
    showToast(positionBothStopOrdersError());
    return { shouldCloseModal: false };
  }

  // If only one of them failed, we still wanna show the success toast for the other one if it was successful
  if (tpOutcome === 'failed') {
    showToast(positionStopOrderError({ stopType: ORDER_STOP_TYPES.GAIN }));
    if (slOutcome === 'unchanged') {
      return { shouldCloseModal: false };
    }
  }

  if (slOutcome === 'failed') {
    showToast(positionStopOrderError({ stopType: ORDER_STOP_TYPES.LOSS }));
    if (tpOutcome === 'unchanged') {
      return { shouldCloseModal: false };
    }
  }

  showToast(
    positionStopOrdersSuccess({
      slOutcome: slOutcome,
      tpOutcome: tpOutcome,
      tpPrice: newTpPrice,
      slPrice: newSlPrice,
      priceDecimals,
    })
  );
  return { shouldCloseModal: tpOutcome !== 'failed' && slOutcome !== 'failed' };
};

export const usePositionTpSlMutation = (product: ProductType | undefined) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const tpMutation = useOrderMutation(product);
  const slMutation = useOrderMutation(product);
  const cancelMutation = useOrderCancelMutation({ showToast: false, optimisticCancel: false }); // Manual toast handling
  const priceDecimals = getDecimalPlaces(product?.tickSize || 0n);

  return useMutation({
    mutationKey: ['positionTpSl', product?.id, selectedSubaccount?.id],
    mutationFn: async ({
      position,
      newTpPrice,
      newSlPrice,
      existingSlOrderId,
      existingTpOrderId,
    }: {
      position: Position;
      newTpPrice: bigint | undefined;
      newSlPrice: bigint | undefined;
      existingSlOrderId: string | undefined;
      existingTpOrderId: string | undefined;
    }): Promise<TpSlMutationResult> => {
      const ordersToCancel = [existingSlOrderId, existingTpOrderId].filter(notNill);
      const needsCancellation = ordersToCancel.length > 0;
      const modifyingTp = newTpPrice !== undefined;
      const modifyingSl = newSlPrice !== undefined;

      if (needsCancellation) {
        try {
          await cancelMutation.mutateAsync(ordersToCancel);
        } catch (error) {
          console.error('Stop order cancellation failed:', error);
          showToast(positionStopOrdersCancellationError());
          // Return early on critical cancellation failure
          return { shouldCloseModal: false };
        }
      }

      const sharedOrderData = {
        discriminator: 'PositionStopOrder',
        type: 'MARKET',
        amount: 0n,
        side: position.side === OrderSide.BUY ? OrderSide.SELL : OrderSide.BUY,
        reduceOnly: true,
        close: true,
      } as const;

      // Create/Modify TP Order
      let tpOpStatus: OperationStatus = 'not_attempted';
      if (modifyingTp) {
        try {
          const orderFormData = {
            ...sharedOrderData,
            stopPrice: newTpPrice,
            stopType: ORDER_STOP_TYPES.GAIN,
          } satisfies PositionStopOrder;
          await tpMutation.mutateAsync(orderFormData);
          tpOpStatus = 'success';
        } catch (error) {
          console.error('TP order creation/modification failed:', error);
          tpOpStatus = 'failed';
        }
      }

      // Create/Modify SL Order
      let slOpStatus: OperationStatus = 'not_attempted';
      if (modifyingSl) {
        try {
          const orderFormData = {
            ...sharedOrderData,
            stopPrice: newSlPrice,
            stopType: ORDER_STOP_TYPES.LOSS,
          } satisfies PositionStopOrder;
          await slMutation.mutateAsync(orderFormData);
          slOpStatus = 'success';
        } catch (error) {
          console.error('SL order creation/modification failed:', error);
          slOpStatus = 'failed';
        }
      }

      // Build Outcome Summary
      const summary: MutationOutcomeSummary = {
        tpOutcome: match(tpOpStatus)
          .returnType<OrderState>()
          .with('success', () => 'created')
          .with('failed', () => 'failed')
          .with('not_attempted', () => (existingTpOrderId ? 'closed' : 'unchanged'))
          .exhaustive(),
        slOutcome: match(slOpStatus)
          .returnType<OrderState>()
          .with('success', () => 'created')
          .with('failed', () => 'failed')
          .with('not_attempted', () => (existingSlOrderId ? 'closed' : 'unchanged'))
          .exhaustive(),
        toastData: {
          newTpPrice,
          newSlPrice,
          priceDecimals,
        },
      };

      // Determine Final Result and Show Toasts
      return determineResultAndToast(summary);
    },
  });
};
